/**
 * User Permissions Tab Component
 *
 * Comprehensive permission viewer showing:
 * - User permission summary
 * - Data scope visualization
 * - Permission matrix
 * - Grant traceability
 * - Permission recalculation tools
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  ExpandMore,
  Person,
  Security,
  Map,
  TableChart,
  Timeline,
  Refresh,
  Business,
  DeleteSweep
} from '@mui/icons-material';

// Common components
import AdminButton from '../../../../../components/common/AdminButton';

// Permission viewer components
import UserPermissionSummary from './permissions/UserPermissionSummary';
import DataScopeViewer from './permissions/DataScopeViewer';
import PermissionTable from './permissions/PermissionTable';
import GrantTraceability from './permissions/GrantTraceability';
import PermissionRecalculationButton from './permissions/PermissionRecalculationButton';

// Services
import { fetchUserPermissions, clearPermissionsCache, clearUserPermissions } from '../../../../../services/authorizationService';
import { fetchUsers as fetchUsersFromDB } from '../../PeopleAdmin/services/userService';
import { grantService } from '../services/grantService';
import { API, graphqlOperation } from 'aws-amplify';
import { listClients } from '../../../../../graphql/queries';
import { permRecalculateUserPermissions } from '../../../../../graphql/mutations';

// Hooks
import { useUser } from '../../../../../hooks/useUser';

export default function UserPermissionsTab() {
  const { user: currentUser } = useUser();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [needsCalculation, setNeedsCalculation] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    dataScope: true,
    matrix: false,
    traceability: false
  });

  // Check if current user is APP_ADMIN
  const isAppAdmin = currentUser?.groupRole === 'APP_ADMIN';

  // Initialize selected client based on user role
  useEffect(() => {
    if (currentUser) {
      if (isAppAdmin) {
        // APP_ADMIN: fetch clients list
        fetchClientsList();
      } else {
        // Non-APP_ADMIN: use their client
        setSelectedClient(currentUser.client);
      }
    }
  }, [currentUser, isAppAdmin]);

  // Fetch users when client is selected
  useEffect(() => {
    if (selectedClient) {
      fetchUsers(selectedClient);
    }
  }, [selectedClient]);

  // Fetch permissions when user is selected
  useEffect(() => {
    if (selectedUserId) {
      loadUserPermissions(selectedUserId);
    }
  }, [selectedUserId]);

  const fetchClientsList = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await API.graphql(
        graphqlOperation(listClients, { limit: 1000 })
      );

      const clientsList = result.data.listClients.items || [];

      // Sort clients alphabetically by clientName
      clientsList.sort((a, b) => a.clientName.localeCompare(b.clientName));

      setClients(clientsList);

      // Auto-select first client if available
      if (clientsList.length > 0 && !selectedClient) {
        setSelectedClient(clientsList[0].clientId);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(`Failed to load clients list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (clientId) => {
    try {
      setLoading(true);
      setError(null);

      if (!clientId) {
        setError('Client information not available');
        return;
      }

      // Fetch users for the specified client from the database
      const usersList = await fetchUsersFromDB(clientId);

      // Transform users to include full name for display
      const transformedUsers = usersList.map(user => ({
        id: user.id,
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        role: user.role,
        group: user.group,
        groupRole: user.groupRole,
        status: user.status
      }));

      // Sort users alphabetically by name
      transformedUsers.sort((a, b) => a.name.localeCompare(b.name));

      setUsers(transformedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(`Failed to load users list: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      console.log('[UserPermissionsTab] Starting loadUserPermissions for userId:', userId, 'clientId:', selectedClient);
      setLoading(true);
      setError(null);
      setNeedsCalculation(false);

      // Fetch user permissions with clientId for optimal composite key lookup
      console.log('[UserPermissionsTab] Fetching user permissions...');
      const userPermissions = await fetchUserPermissions(userId, selectedClient);
      console.log('[UserPermissionsTab] User permissions result:', userPermissions);

      if (!userPermissions) {
        // No permissions cache found - needs calculation
        console.log('[UserPermissionsTab] No permissions cache found - setting needsCalculation=true');
        setNeedsCalculation(true);
        setPermissions(null);
        setGrants([]);
        return;
      }

      console.log('[UserPermissionsTab] Permissions cache found:', {
        userId: userPermissions.userId,
        securityVersion: userPermissions.securityVersion,
        calculatedAt: userPermissions.calculatedAt,
        hasNavigation: !!userPermissions.navigation,
        hasDataScope: !!userPermissions.dataScope
      });

      setPermissions(userPermissions);
      setNeedsCalculation(false);

      // Fetch applicable grants for this user
      try {
        console.log('[UserPermissionsTab] Fetching applicable grants for userId:', userId, 'clientId:', selectedClient);
        const applicableGrantsResponse = await grantService.getApplicableGrants(
          userId,
          selectedClient,
          { includeInactive: false }
        );
        console.log('[UserPermissionsTab] Applicable grants response:', applicableGrantsResponse);

        if (applicableGrantsResponse?.grants) {
          // Ensure grants is an array
          const grantsArray = Array.isArray(applicableGrantsResponse.grants)
            ? applicableGrantsResponse.grants
            : [];
          console.log('[UserPermissionsTab] Setting grants, count:', grantsArray.length);
          setGrants(grantsArray);
        } else {
          console.log('[UserPermissionsTab] No grants in response, setting empty array');
          setGrants([]);
        }
      } catch (grantErr) {
        console.error('[UserPermissionsTab] Error fetching grants:', grantErr);
        // Don't fail the whole operation if grants fetch fails
        setGrants([]);
      }

    } catch (err) {
      console.error('[UserPermissionsTab] Error loading user permissions:', err);
      setError(`Failed to load permissions: ${err.message}`);
      setPermissions(null);
      setGrants([]);
      setNeedsCalculation(false);
    } finally {
      console.log('[UserPermissionsTab] loadUserPermissions complete');
      setLoading(false);
    }
  };

  const handleClientChange = (event) => {
    const newClient = event.target.value;
    setSelectedClient(newClient);
    // Reset user selection when client changes
    setSelectedUserId('');
    setUsers([]);
    setPermissions(null);
    setGrants([]);
    setError(null);
  };

  const handleUserChange = (event) => {
    setSelectedUserId(event.target.value);
    setPermissions(null);
    setGrants([]);
    setError(null);
  };

  const handleRecalculate = async (options) => {
    try {
      setRecalculating(true);
      setError(null);
      setNeedsCalculation(false);

      console.log('Starting permission recalculation for user:', selectedUserId);

      // Clear the permissions cache
      clearPermissionsCache(selectedUserId);

      // Call the Lambda function to recalculate permissions
      const result = await API.graphql(
        graphqlOperation(permRecalculateUserPermissions, {
          userId: selectedUserId,
          clientId: selectedClient,
          action: 'RECALCULATE',
          source: 'USER_PERMISSIONS_TAB'
        })
      );

      console.log('Recalculation result:', result);

      const recalcResult = result.data.permRecalculateUserPermissions;

      if (recalcResult.success) {
        // Reload permissions to show the newly calculated data
        await loadUserPermissions(selectedUserId);
      } else {
        setError('Failed to recalculate permissions. Please try again.');
      }
    } catch (err) {
      console.error('Error recalculating permissions:', err);
      setError(`Failed to recalculate permissions: ${err.message}`);
      setNeedsCalculation(true);
    } finally {
      setRecalculating(false);
    }
  };

  const handleClearPermissions = async () => {
    // Confirm with user since this is destructive
    const confirmed = window.confirm(
      `Are you sure you want to clear all permissions for ${selectedUser?.name || 'this user'}?\n\n` +
      'This will:\n' +
      '• Delete the permissions cache from DynamoDB\n' +
      '• Clear the local storage cache\n' +
      '• Require recalculation to restore permissions\n\n' +
      'This action is safe and can be undone by recalculating permissions.'
    );

    if (!confirmed) {
      return;
    }

    try {
      setClearing(true);
      setError(null);

      console.log('[UserPermissionsTab] Clearing permissions for user:', selectedUserId);

      const result = await clearUserPermissions(selectedUserId, selectedClient);

      console.log('[UserPermissionsTab] Clear permissions result:', result);

      if (result.success) {
        // Build success message
        const messages = [];
        if (result.clearedDynamoDB) {
          messages.push('DynamoDB cache deleted');
        }
        if (result.clearedLocalStorage) {
          messages.push('localStorage cache cleared');
        }
        if (!result.clearedDynamoDB && !result.clearedLocalStorage) {
          messages.push('No permissions found to clear (already cleared or never existed)');
        }

        console.log('[UserPermissionsTab] Permissions cleared successfully:', messages.join(', '));

        // Update UI state to show "needs calculation"
        setPermissions(null);
        setGrants([]);
        setNeedsCalculation(true);

        // Show brief success message (optional - could use a Snackbar if available)
        alert(`Permissions cleared successfully!\n\n${messages.join('\n')}`);
      } else {
        setError(`Failed to clear permissions: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      console.error('[UserPermissionsTab] Error clearing permissions:', err);
      setError(`Failed to clear permissions: ${err.message}`);
    } finally {
      setClearing(false);
    }
  };

  const handleGrantClick = (grantId) => {
    // TODO: Navigate to grant editor
    console.log('Navigate to grant:', grantId);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  // Debug logs for render state
  console.log('[UserPermissionsTab] Render state:', {
    selectedUserId,
    hasPermissions: !!permissions,
    needsCalculation,
    loading,
    error,
    grantsCount: grants.length,
    selectedUser: selectedUser ? `${selectedUser.name} (${selectedUser.id})` : 'none'
  });

  return (
    <Box>
      {/* Client and User Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
          {/* Client Selector - Only for APP_ADMIN */}
          {isAppAdmin ? (
            <FormControl sx={{ flex: '1 1 auto', minWidth: 200 }}>
              <InputLabel>Select Client</InputLabel>
              <Select
                value={selectedClient}
                onChange={handleClientChange}
                label="Select Client"
                sx={{
                  height: '40px',
                  '& .MuiSelect-select': {
                    height: '40px',
                    minHeight: '40px !important',
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '16.5px',
                    paddingBottom: '16.5px'
                  }
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.clientId}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Business fontSize="small" />
                      <Box sx={{ lineHeight: 1.2 }}>
                        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.2, mb: 0 }}>
                          {client.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                          {client.clientId}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            // Display current client for non-APP_ADMIN users
            selectedClient && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'nowrap' }}>
                <Business fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Client: <strong>{currentUser?.client}</strong>
                </Typography>
              </Box>
            )
          )}

          {/* User Selector */}
          <FormControl sx={{ flex: '1 1 auto', minWidth: 200 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              onChange={handleUserChange}
              label="Select User"
              disabled={!selectedClient}
              sx={{
                height: '40px',
                '& .MuiSelect-select': {
                  height: '40px',
                  minHeight: '40px !important',
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '16.5px',
                  paddingBottom: '16.5px'
                }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Person fontSize="small" />
                    <Box sx={{ flexGrow: 1, lineHeight: 1.2 }}>
                      <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.2, mb: 0 }}>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                        {user.userName} • {user.email}
                      </Typography>
                    </Box>
                    {user.status && user.status !== 'ACTIVE' && (
                      <Chip
                        label={user.status}
                        size="small"
                        color={user.status === 'SUSPENDED' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedUserId && permissions && (
            <Box sx={{ flexShrink: 0 }}>
              <PermissionRecalculationButton
                userId={selectedUserId}
                userName={selectedUser?.name}
                isStale={permissions?.isStale}
                lastCalculated={permissions?.calculatedAt}
                onRecalculate={handleRecalculate}
                isLoading={recalculating}
                mode="single"
              />
            </Box>
          )}

          {selectedUserId && (
            <AdminButton
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweep />}
              onClick={handleClearPermissions}
              disabled={clearing || loading || recalculating}
              sx={{ flexShrink: 0 }}
            >
              {clearing ? 'Clearing...' : 'Clear'}
            </AdminButton>
          )}
        </Stack>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* No User Selected */}
      {!selectedUserId && !loading && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a user to view their permissions
          </Typography>
        </Paper>
      )}

      {/* No Permissions - Needs Calculation */}
      {selectedUserId && needsCalculation && !loading && (() => {
        console.log('[UserPermissionsTab] Rendering "Needs Calculation" view');
        return (
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <Security sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Permissions Not Calculated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This user's permissions have not been calculated yet. Click the button below to calculate permissions based on their assigned grants.
            </Typography>
            <AdminButton
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRecalculate}
              disabled={recalculating}
            >
              {recalculating ? 'Calculating...' : 'Calculate Permissions'}
            </AdminButton>
            {selectedUser && (
              <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
                User: {selectedUser.name} ({selectedUser.userName})
              </Typography>
            )}
          </Paper>
        );
      })()}

      {/* Permissions Display */}
      {selectedUserId && permissions && !loading && (() => {
        console.log('[UserPermissionsTab] Rendering permissions display view');
        return (
        <Stack spacing={2}>
          {/* Permission Summary */}
          <Accordion
            expanded={expandedSections.summary}
            onChange={() => toggleSection('summary')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Security color="primary" />
                <Typography variant="h6">Permission Summary</Typography>
                {permissions.isStale && (
                  <Chip label="Stale" color="warning" size="small" variant="outlined" />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <UserPermissionSummary
                permissions={permissions}
                grantCount={grants.length}
                securityVersion={permissions.securityVersion}
                user={selectedUser}
                grants={grants}
              />
            </AccordionDetails>
          </Accordion>

          {/* Data Scope */}
          <Accordion
            expanded={expandedSections.dataScope}
            onChange={() => toggleSection('dataScope')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Map color="primary" />
                <Typography variant="h6">Data Scope</Typography>
                <Chip
                  label={permissions.dataScope?.scopeType || 'ASSIGNMENT_SCOPED'}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <DataScopeViewer
                dataScope={permissions.dataScope}
                user={selectedUser}
              />
            </AccordionDetails>
          </Accordion>

          {/* Permission Matrix */}
          <Accordion
            expanded={expandedSections.matrix}
            onChange={() => toggleSection('matrix')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TableChart color="primary" />
                <Typography variant="h6">Permission Matrix</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <PermissionTable
                permissions={permissions.navigation}
                onGrantClick={handleGrantClick}
              />
            </AccordionDetails>
          </Accordion>

          {/* Grant Traceability */}
          <Accordion
            expanded={expandedSections.traceability}
            onChange={() => toggleSection('traceability')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Timeline color="primary" />
                <Typography variant="h6">Grant Traceability</Typography>
                <Chip
                  label={`${grants.length} grants`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <GrantTraceability
                grants={grants}
                permissions={permissions.navigation}
                onGrantEdit={handleGrantClick}
              />
            </AccordionDetails>
          </Accordion>
        </Stack>
        );
      })()}
    </Box>
  );
}
